import { Box, DataList, Flex, SvgIcon } from '@volue/wave-react';
import React from 'react';

import useFeatureToggles from '@/common/hooks/feature-toggles';
import type {
  DomainGuid,
  GridElementSectionWithQuickAccessResult,
  GridElementWithQuickAccessResult
} from '@/features/execute-calculation-set/overmind/calculation';

import { CollapsibleContainer, isVisible } from '../CollapsibleCard';

export type GridElementsTreeSectionProps = {
  header: string;
  gridElements: GridElementWithQuickAccessResult[];
  type: string;
  onGridElementClick: (id: DomainGuid) => void;
};
const GridElementsTreeSection: React.FunctionComponent<
  GridElementsTreeSectionProps
> = props => {
  const featureFlags = useFeatureToggles();
  return (
    <CollapsibleContainer expandedByDefault>
      {(toggle, currentState) => (
        <Box data-testid={`${props.type}-container`}>
          <Flex
            cross="center"
            data-testid={`${props.type}-header`}
            css={{ color: '$colorAccent70', fontWeight: 'bolder' }}
            onClick={() => toggle()}
          >
            <SvgIcon
              iconName={
                currentState === 'EXPANDED' ? 'chevronDown' : 'chevronUp'
              }
            />
            <span>{props.header}</span>
          </Flex>
          <Flex
            flow={'column'}
            data-testid={`${props.type}-grid-elements`}
            gap={'spacingXs'}
            css={{ marginLeft: '$spacingXl' }}
            className={isVisible(currentState) ? 'is-visible' : 'is-hidden'}
          >
            {props.gridElements.map(gridElement => (
              <Box
                data-testid={`${props.type}-grid-element-${gridElement.objectId}`}
                onClick={() => props.onGridElementClick(gridElement.objectId)}
                className="is-actionable"
              >
                {getTitle(gridElement)}
                {gridElement.evaluatedResults.type ===
                  'EVALUATED_RESULT_FOR_QUICK_ACCESS' && (
                  <DataList css={{ fontSize: 'small' }} ratio={[4, 1]}>
                    {gridElement.evaluatedResults.evaluatedResults.map(
                      evaluatedResult => (
                        <Flex as={DataList.Row} main="space-between">
                          <DataList.Label css={{ fontWeight: 'normal' }}>
                            {evaluatedResult.label}
                          </DataList.Label>
                          <Flex main={'end'} as={DataList.Value}>
                            <Box
                              css={{
                                color: '$colorRed100',
                                fontWeight: 'bolder'
                              }}
                            >
                              {evaluatedResult.value}
                            </Box>
                          </Flex>
                        </Flex>
                      )
                    )}
                  </DataList>
                )}
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </CollapsibleContainer>
  );

  function getTitle(
    gridElement: GridElementWithQuickAccessResult
  ): React.ReactNode {
    if (featureFlags.isShowingGridElementTitleEnabled())
      return gridElement.title;
    return gridElement.label;
  }
};

export type GridElementsTreeProps = {
  sections: GridElementSectionWithQuickAccessResult[];
  onGridElementClicked: (domainId: DomainGuid) => void;
};

export function GridElementsTree(props: GridElementsTreeProps) {
  return (
    <Flex flow={'column'} main={'center'} gap={'spacingXs'}>
      {props.sections.map(section => (
        <GridElementsTreeSection
          key={section.header}
          header={section.header}
          gridElements={section.gridElements}
          type={section.type}
          onGridElementClick={props.onGridElementClicked}
        />
      ))}
    </Flex>
  );
}
